// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// O protocolo do arrasto de um pedido para o mapa (RF-7, operação manual)
// ============================================================================
//
// 📗 Pedido do cliente (2026-08-06): *«eu quero que exista a opção também de permitir
// arrastar os pedidos de agendamento manualmente para onde quiser»*.
//
// ⚠️ **O QUE O ARRASTO ESCOLHE É A VIATURA, NÃO AS DATAS** — e isto não é uma limitação
// da implementação, é o domínio:
//
//   O grupo viaja nos dias que o pedido diz. Um voo chega no dia 3 e o cliente parte no
//   dia 7; largar o cartão sobre a coluna do dia 12 **não muda o voo**. As datas são do
//   pedido; o que o planeador está a decidir ao arrastar é **que autocarro mobilizar**,
//   que é exactamente a decisão que o mapa existe para informar.
//
//   ⚠️ Mas o gesto não é ignorado em silêncio: se a coluna onde se larga cair fora do
//   período do pedido, a janela de confirmação **di-lo com as duas datas à vista**.
//   Ignorar em silêncio a metade do gesto que não se pode honrar seria pior do que
//   recusar o gesto todo — o planeador acreditaria que reservou os dias que apontou.
//
// ⚠️ PORQUE UM FICHEIRO PRÓPRIO
//   Este é o único contrato entre a lista de pedidos e a grelha, e as duas não se
//   conhecem. Deixá-lo em qualquer um dos lados fazia o outro depender do ecrã inteiro.
// ============================================================================

import { codigoDoDestinoFinal } from './destino-final-no-ecra.js';
import type { Pedido } from './pedido.js';

/**
 * O tipo MIME do arrasto.
 *
 * ⚠️ Existe para o `dragover` da grelha poder decidir se aceita **sem ler os dados** —
 * durante o arrasto os navegadores só expõem `dataTransfer.types`, e o conteúdo apenas
 * no `drop`. Sem um tipo próprio, a grelha aceitaria o arrasto de qualquer texto ou
 * ficheiro que passasse por cima dela.
 *
 * Em minúsculas porque é assim que os navegadores normalizam `types`.
 */

export const TIPO_ARRASTO = 'application/x-xlv-pedido';

/** O que a grelha precisa de saber sobre o pedido que lhe largaram em cima. */
export interface PedidoArrastado {
  readonly id: string;
  /** ⚠️ O ID, e não só a sigla: é o que a reserva grava, e faltava. */
  readonly clienteId: string;
  readonly clienteSigla: string;
  /** Índices de dia do ano. São **do pedido** — ver o cabeçalho deste ficheiro. */
  readonly dia0: number;
  readonly dia1: number;
  readonly dataInicio: string;
  readonly dataFim: string;
  readonly plano: string;
  readonly itinerario: string;
  readonly referencia: string;
  readonly lotacao: number;
  /** Localidade de recolha, `''` quando o pedido não a diz. */
  readonly localidade: string;
  /**
   * ⚠️⚠️ **A hora a que o grupo embarca (B13) — `'HH:MM'`, `''` ou ausente = não se sabe.**
   *
   * 📌 Vai com o arrasto porque é **metade da conta do dia em trânsito**: o dia do embarque não
   * conta como inteiro (às 05:25 vale quase nada, às 22:50 vale quase tudo), e sem ela a
   * validação assumia o dia todo. Medido: **77 de 78** pedidos por planear têm a hora.
   *
   * ⚠️ **Opcional de propósito** — um arrasto vindo de uma versão anterior do ecrã continua a
   * funcionar, e aí `mobilizacaoViavel` fica com o comportamento anterior palavra por palavra
   * (o dia inteiro), que é o que **não** decide contra a viatura.
   */
  /**
   * ---- ⚠️⚠️ A6-bis · ONDE A NOSSA VIAGEM LARGA — e é o ramo ESPELHO da rotação -------------
   *
   * 📗 Cliente, 2026-09-01: *«um dia de rotação aparece como motorista indisponível quando pode
   *    fazer a viagem seguinte da rotação?»* — **aparecia, nos dois ramos.**
   *
   * 📌 A `localidade` acima é a **recolha**, e serve o ramo 1 (o nosso `dia0` no dia em que outra
   * viagem **acaba**). O **espelho** é o nosso `dia1` no dia em que outra **começa**, e o
   * predicado dele (`rotacaoEspelhoPossivelNoDia`) pergunta pela **largada**. Sem ela recusava
   * sempre — e o espelho é **45,5 %** das oportunidades de rotação.
   *
   * ⚠️ **Opcional de propósito**, como a `horaRecolha`: um arrasto vindo de uma versão anterior
   * do ecrã continua a funcionar, e aí o espelho fica com o comportamento anterior palavra por
   * palavra (`-1` → o predicado recusa), que é a direcção segura.
   */
  readonly localidadeFinal?: string;
  readonly horaRecolha?: string;
  /** Texto livre das características exigidas (F2). Vai para a confirmação. */
  readonly requisitos: string;
}

/**
 * ---- 🔴 O QUE UM PEDIDO LEVA AO SER ARRASTADO — UMA formulação, dois sítios (2026-10-08) ------
 *
 * 📗 Cliente, 2026-10-08: as barras da secção «por planear» do fundo do mapa *«tem de dar para
 * arrastando mover»* — o mesmo gesto do cartão da lista. 📌 Os campos saíam escritos à mão no
 * `onDragStart` de `Pedidos.tsx`; com um segundo sítio a arrastar, uma cópia que esquecesse a
 * `localidadeFinal` (o ramo espelho da rotação, 45,5 % das oportunidades) ou a `horaRecolha`
 * (B13) validava contra outro pedido — regra 8. Guardado por `arrasto-do-pedido.test.ts`.
 */
export function arrastoDoPedido(pedido: Pedido): PedidoArrastado {
  return {
    id: pedido.id,
    clienteId: pedido.clienteId,
    clienteSigla: pedido.clienteSigla,
    dia0: pedido.dia0,
    dia1: pedido.dia1,
    dataInicio: pedido.dataInicio,
    dataFim: pedido.dataFim,
    plano: pedido.plano,
    itinerario: pedido.itinerario,
    referencia: pedido.referencia,
    lotacao: pedido.lotacao,
    localidade: pedido.recolha?.localidade ?? '',
    // ⚠️⚠️ **A6-bis · a LARGADA vai também** — é o que o ramo ESPELHO da rotação pergunta (o
    //    nosso último dia no dia em que outra viagem começa). Sem ela o predicado recusava sempre,
    //    e o espelho é 45,5 % das oportunidades. ⚠️⚠️ E é o destino FINAL, registado ou presumido
    //    (2026-09-29) — o MESMO que o «Planear» envia ao motor e grava na reserva.
    localidadeFinal: codigoDoDestinoFinal(pedido),
    // ⚠️ **A hora de recolha VAI COM O ARRASTO** — é metade da conta do dia em trânsito (B13).
    //    Sem ela a validação assume o dia do embarque inteiro e conta menos estrada do que existe.
    horaRecolha: (pedido.recolha?.hora ?? '').slice(0, 5),
    requisitos: pedido.requisitos,
  };
}

/** Arma o arrasto. Chamado no `onDragStart` do cartão do pedido. */
export function iniciarArrasto(dt: DataTransfer, p: PedidoArrastado): void {
  dt.setData(TIPO_ARRASTO, JSON.stringify(p));
  // ⚠️ `copy` e não `move`: o pedido **não sai da lista**. Sai quando a reserva existir,
  // e o estado do pedido é derivado das alocações (migração 004) — não é o arrasto que
  // o muda. `move` prometia ao utilizador uma coisa que não acontece.
  dt.effectAllowed = 'copy';
}

/** `true` se o que está a passar por cima é um pedido nosso, e não texto ou ficheiros. */
export function eArrastoDePedido(dt: DataTransfer | null): boolean {
  if (dt === null) return false;
  return [...dt.types].includes(TIPO_ARRASTO);
}

/**
 * Lê o pedido largado. `null` se não for um arrasto nosso ou vier corrompido.
 *
 * ⚠️ O `try` não é defesa contra o impossível: o `dataTransfer` é atravessável por
 * qualquer página e o conteúdo pode não ser o nosso JSON.
 */
export function pedidoArrastadoDe(dt: DataTransfer | null): PedidoArrastado | null {
  if (dt === null) return null;
  const bruto = dt.getData(TIPO_ARRASTO);
  if (bruto === '') return null;
  try {
    const p = JSON.parse(bruto) as PedidoArrastado;
    if (typeof p.id !== 'string' || !Number.isFinite(p.dia0)) return null;
    return p;
  } catch {
    return null;
  }
}

// ============================================================================
// ⚠️⚠️ O SEGUNDO PROTOCOLO — arrastar um serviço que JÁ ESTÁ no mapa (B30, T-74)
// ============================================================================
//
// 📗 Cliente, 2026-08-30: *«mover facilmente»* — e T-74, *«mover um bloco com o rato»*.
//
// ⚠️⚠️ **TIPO PRÓPRIO E NÃO O MESMO, e a razão não é arrumação: são gestos com efeitos
//    OPOSTOS.** Arrastar da lista **cria** uma reserva; arrastar uma barra **move** um serviço
//    que já existe, e nesse a viatura de origem fica livre. 📌 Com um tipo só, o `drop` teria
//    de descobrir de que lado veio pelo conteúdo — e o dia em que se enganasse criava um
//    serviço duplicado em vez de mover um.
//
// ⚠️ **E o `effectAllowed` é `move` e não `copy`**, ao contrário do outro: aqui o serviço
//    **sai mesmo** da linha de origem. O cursor do navegador diz ao planeador qual dos dois
//    gestos está a fazer antes de largar, sem nada escrito no ecrã.
export const TIPO_ARRASTO_SERVICO = 'application/x-xlv-servico';

/** O serviço que está a ser arrastado de uma linha do mapa para outra. */
export interface ServicoArrastado {
  /** Índice da viatura de ORIGEM na grelha. */
  readonly viatura: number;
  /** Os dias da corrida — índices de dia do ano, inclusive. */
  readonly dia0: number;
  readonly dia1: number;
  /**
   * ⚠️ **O dia onde o planeador AGARROU**, que pode não ser o primeiro da corrida.
   *
   * 📌 É o atalho do «a partir daqui» na janela de confirmação: medido, **11 de 12**
   * partições reais movem a **cauda**. Quem agarra no dia 3 de uma viagem de 5 quer, quase
   * sempre, mover do 3 ao fim.
   */
  readonly diaAgarrado: number;
  /**
   * ⚠️⚠️ **A REFERÊNCIA DA CORRIDA, e é obrigatória.**
   *
   * 📌 Num dia de rotação há **duas** linhas, e `mover.ts` exige saber qual delas se move —
   * sem ela, «a viagem que parte ia para o autocarro novo atrás da que acaba». `''` quando o
   * serviço não tem referência (o «Planear» não a exige e a reserva grava `NULL`), e aí o
   * servidor cai no comportamento antigo, que é o correcto num dia normal.
   */
  readonly referencia: string;
  /** Só para a janela de confirmação poder nomear o que se move. */
  readonly sigla: string;
}

/** Arma o arrasto de uma barra do mapa. Chamado no `onDragStart` delegado da grelha. */
export function iniciarArrastoDeServico(dt: DataTransfer, s: ServicoArrastado): void {
  dt.setData(TIPO_ARRASTO_SERVICO, JSON.stringify(s));
  dt.effectAllowed = 'move';
}

/** `true` se o que passa por cima é um serviço do mapa. */
export function eArrastoDeServico(dt: DataTransfer | null): boolean {
  if (dt === null) return false;
  return [...dt.types].includes(TIPO_ARRASTO_SERVICO);
}

/** Lê o serviço largado. `null` se não for nosso ou vier corrompido. */
export function servicoArrastadoDe(dt: DataTransfer | null): ServicoArrastado | null {
  if (dt === null) return null;
  const bruto = dt.getData(TIPO_ARRASTO_SERVICO);
  if (bruto === '') return null;
  try {
    const s = JSON.parse(bruto) as ServicoArrastado;
    if (!Number.isFinite(s.viatura) || !Number.isFinite(s.dia0) || !Number.isFinite(s.dia1)) {
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

// ============================================================================
// 🔴 2026-10-08 · ARRASTAR UMA VIAGEM EM ESPERA PARA UMA VIATURA
// ============================================================================
//
// 📗 Cliente, 2026-10-08: nas secções do fundo *«tem de dar para arrastando mover algo»* — *«eles
//    estão habituados à extrema facilidade de alterações do Excel»*. Largar uma barra «em espera»
//    numa linha de viatura abre o MESMO «Trazer de volta» do clique, já com essa viatura escolhida
//    — não há segundo caminho de escrita, só um atalho para a escolha.
//
// ⚠️ **Tipo PRÓPRIO**, e não o do serviço: uma viagem em espera não tem viatura de origem (o
//    `ServicoArrastado.viatura` seria inventado), e o que a identifica é a referência mais o
//    intervalo — exactamente o que `/api/plano/trazer-de-espera` pede.
export const TIPO_ARRASTO_ESPERA = 'application/x-xlv-espera';

/** A viagem em espera arrastada — a mesma forma do `aTrazerDeEspera` do mapa. */
export interface EsperaArrastada {
  readonly referencia: string;
  /** O intervalo da viagem no ANO (`extentoDoServico`), e não o recorte da barra visível. */
  readonly dia0: number;
  readonly dia1: number;
}

export function iniciarArrastoDeEspera(dt: DataTransfer, e: EsperaArrastada): void {
  dt.setData(TIPO_ARRASTO_ESPERA, JSON.stringify(e));
  dt.effectAllowed = 'move';
}

/** `true` se o que passa por cima é uma viagem em espera. */
export function eArrastoDeEspera(dt: DataTransfer | null): boolean {
  if (dt === null) return false;
  return [...dt.types].includes(TIPO_ARRASTO_ESPERA);
}

/** Lê a viagem em espera largada. `null` se não for nossa ou vier corrompida. */
export function esperaArrastadaDe(dt: DataTransfer | null): EsperaArrastada | null {
  if (dt === null) return null;
  const bruto = dt.getData(TIPO_ARRASTO_ESPERA);
  if (bruto === '') return null;
  try {
    const e = JSON.parse(bruto) as EsperaArrastada;
    if (typeof e.referencia !== 'string' || !Number.isFinite(e.dia0) || !Number.isFinite(e.dia1)) {
      return null;
    }
    return e;
  } catch {
    return null;
  }
}
